// Ad-copy bridge pages served at /explore/{brand}-{version}.
//
// Each entry is one piece of ad copy for one advertiser. The page layout is
// fixed (site nav → hero CTA → short paragraph section → footer); only the
// copy, imagery, and outbound destination change between entries.
//
// To add another template, copy an entry and give it a new key using the
// same {brand}-{version} shape, e.g. 'uagc-v2' or 'gcu-v1'. Keys are matched
// case-insensitively, so /explore/UAGC-v1 resolves to 'uagc-v1'.
export interface ExploreBridge {
  // {brand}-{version} slug, lowercase.
  slug: string
  // Advertiser this ad copy promotes — used in meta tags and the disclosure.
  brand: string
  // Where the CTA sends the user. Can be an internal path (e.g. an
  // advertiser page on this site) or a full external URL. Ignored when
  // `clicksSearch` is set.
  destinationUrl?: string
  // When set, the CTA builds a Media-Matchers Clicks Search redirect and
  // forwards any lead data present on this page's URL (same accepted
  // parameters and value mappings as the /request-information/v1 form).
  clicksSearch?: {
    // Used when the incoming URL has neither mPubGroupID nor the
    // mPubID + mPubCampaignID pair.
    defaultPubGroupID: string
    // Optional advertiser brand id for this ad copy.
    mBrandID?: string
    // Program id sent when the incoming URL doesn't supply mProgram.
    defaultMProgram?: string
  }
  // Browser tab title + meta description.
  metaTitle: string
  metaDescription: string
  // Hero
  heroImage: string
  heroImageAlt: string
  cardHeadline: string
  cardSubhead: string
  ctaLabel: string
  // Short paragraph section below the hero.
  sectionHeading: string
  paragraphs: string[]
  // Proof points shown as a row beneath the intro copy. Falls back to the
  // shared national education stats below.
  highlights?: { stat: string; label: string; body: string; note?: string }[]
  // One-line nudge shown beside the repeat call to action.
  closingLine?: string
  // Small print shown under the paragraph section.
  disclosure?: string
  // 'default' (site nav → hero CTA → paragraphs → site footer) or
  // 'branded', which mirrors the advertiser page for the school named by
  // `universitySlug`: school logo bar, brand-colored hero with highlights,
  // stats strip, feature grid, and a closing brand CTA band.
  layout?: 'default' | 'branded'
  // Advertiser record in data/universityData.ts supplying the branding and
  // marketing content for the 'branded' layout.
  universitySlug?: string
}
// National education outcomes shown on every default-layout bridge page
// unless an entry supplies its own. Superscripts map to EDUCATION_CITATIONS.
export const DEFAULT_HIGHLIGHTS = [
  {
    stat: '+$30k',
    label: 'a year',
    body: "Graduates with a bachelor's degree earn about $30,000 more per year than those with only a high school diploma.",
    note: '1',
  },
  {
    stat: '4.1m',
    label: 'graduates',
    body: 'More than 4 million people graduate from college each year in the United States.',
    note: '2',
  },
  {
    stat: '6.5m',
    label: 'online students',
    body: 'More than 6.5 million students have enrolled in a distance education course at a college or university.',
    note: '3',
  },
]

// Sources backing DEFAULT_HIGHLIGHTS, rendered in the small citations block.
export const EDUCATION_CITATIONS = {
  links: [
    'https://www.bls.gov/emp/chart-unemployment-earnings-education.htm',
    'https://educationdata.org/number-of-college-graduates',
    'https://nces.ed.gov/FastFacts/display.asp?id=80',
  ],
  notes: [
    '-Data updated from BLS on February 25, 2025',
    '* Some students may be able to use prior experience for school program credit',
    '** Based on national data, not school-specific information. Conditions in your area may vary.',
  ],
}

export const exploreBridges: Record<string, ExploreBridge> = {
  'uagc-v1': {
    slug: 'uagc-v1',
    brand: 'University of Arizona Global Campus',
    clicksSearch: {
      defaultPubGroupID: 'PCGP3406855604',
    },
    metaTitle: 'Explore Online Degrees at UAGC | FindOnlineDegrees',
    metaDescription:
      'See how the University of Arizona Global Campus supports working adults with flexible online degree programs.',
    heroImage:
      'https://cdn.magicpatterns.com/patterns/generated-images/17c98d59-2e1d-42a3-b403-680119e82ff3.jpg',
    heroImageAlt:
      'Adult student smiling while taking an online class on a laptop at home',
    cardHeadline: 'Get Matched With Your Perfect School Today!',
    cardSubhead: 'Financial aid may be available for those who qualify',
    ctaLabel: 'Start Now!',
    sectionHeading: 'Why Choose UAGC for Your Online Degree?',
    paragraphs: [
      "The University of Arizona Global Campus builds its online programs around the realities of adult life. Courses are delivered fully online in focused terms, so you can keep working while you earn your degree — and start without pausing everything else you're responsible for.",
      'UAGC offers a wide range of bachelor’s and master’s programs across business, technology, healthcare administration, education, and more. Academic advisors work with you from your first question through graduation to help you stay on track toward your goals.',
    ],
    disclosure:
      'FindOnlineDegrees is an advertising-supported site. We may receive compensation when you request information from the schools featured here.',
  },
  // Branded version of the UAGC ad copy — mirrors the look of the
  // /universities/university-of-arizona-global-campus advertiser page.
  'uagc-v2': {
    slug: 'uagc-v2',
    brand: 'University of Arizona Global Campus',
    layout: 'branded',
    universitySlug: 'university-of-arizona-global-campus',
    clicksSearch: {
      defaultPubGroupID: 'PCGP3406855604',
    },
    metaTitle: 'University of Arizona Global Campus Online Degrees',
    metaDescription:
      'Education tailored to your life. Explore flexible online degree programs from the University of Arizona Global Campus.',
    heroImage:
      'https://cdn.magicpatterns.com/patterns/generated-images/17c98d59-2e1d-42a3-b403-680119e82ff3.jpg',
    heroImageAlt:
      'Adult student smiling while taking an online class on a laptop at home',
    cardHeadline: 'Interested in learning more about our degrees?',
    cardSubhead: 'Take the next step toward a degree designed around your life',
    ctaLabel: 'Get Program Information',
    sectionHeading: 'Why Choose UAGC for Your Online Degree?',
    paragraphs: [
      'UAGC makes it easier to finish what you started. Take one course at a time in an accelerated 5-, 6-, or 9-week format, access the online classroom 24/7, and learn from expert faculty with real-world experience in their fields.',
      'Standardized test scores aren’t required to enroll, approved college credits may transfer toward your bachelor’s program, and a three-week trial period helps you decide whether UAGC is the right fit before you commit.',
    ],
    disclosure:
      'FindOnlineDegrees is an advertising-supported site. We may receive compensation when you request information from the schools featured here.',
  },
  // UMA ad copy — default layout (site nav → hero CTA → paragraphs → footer).
  'uma-v1': {
    slug: 'uma-v1',
    brand: 'Ultimate Medical Academy',
    clicksSearch: {
      defaultPubGroupID: 'PCGP3406855604',
      mBrandID: '11154',
      defaultMProgram: '500',
    },
    metaTitle: 'Explore Healthcare Programs at UMA | FindOnlineDegrees',
    metaDescription:
      'See how Ultimate Medical Academy prepares students for healthcare careers with flexible online programs and personalized support.',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/br5nxfXurTya7wJ6Dtqd98/Medical.png',
    heroImageAlt:
      'Healthcare professional in scrubs smiling in a clinical setting',
    cardHeadline: 'Start Your Healthcare Career Training Today!',
    cardSubhead: 'Financial aid may be available for those who qualify',
    ctaLabel: 'Start Now!',
    sectionHeading: 'Why Choose UMA for Healthcare Career Training?',
    paragraphs: [
      'Ultimate Medical Academy focuses on one thing: preparing students for careers in healthcare. Coursework is delivered online and built for adult learners, so you can study around work and family instead of putting your life on hold.',
      'From your first conversation through graduation and career preparation, UMA pairs you with advisors and instructors who know your goals. Financial aid guidance is available for those who qualify, and UMA is institutionally accredited by the Accrediting Bureau of Health Education Schools (ABHES).',
    ],
    disclosure:
      'FindOnlineDegrees is an advertising-supported site. We may receive compensation when you request information from the schools featured here.',
  },
  // Branded UMA ad copy — mirrors the look of the
  // /universities/ultimate-medical-academy advertiser page.
  'uma-v2': {
    slug: 'uma-v2',
    brand: 'Ultimate Medical Academy',
    layout: 'branded',
    universitySlug: 'ultimate-medical-academy',
    clicksSearch: {
      defaultPubGroupID: 'PCGP3406855604',
      mBrandID: '11154',
      defaultMProgram: '500',
    },
    metaTitle: 'Ultimate Medical Academy Healthcare Programs',
    metaDescription:
      'Healthcare career training designed around your life. Explore online associate degree programs from Ultimate Medical Academy.',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/br5nxfXurTya7wJ6Dtqd98/Medical.png',
    heroImageAlt:
      'Healthcare professional in scrubs smiling in a clinical setting',
    cardHeadline: 'Interested in a career in healthcare?',
    cardSubhead:
      'Learn how UMA can help you prepare for the healthcare role you want',
    ctaLabel: 'Get Program Information',
    sectionHeading: 'Healthcare Education Built Around Your Life',
    paragraphs: [
      'UMA offers online associate degree programs across medical assisting, healthcare management, health sciences, medical billing and coding, and more — each designed to build practical, job-ready knowledge for the healthcare workforce.',
      'You’ll have support at every stage: advisors who help you choose a program, instructors who teach in an engaging online environment, and career services that help you prepare for interviews and your job search after graduation.',
    ],
    disclosure:
      'FindOnlineDegrees is an advertising-supported site. We may receive compensation when you request information from the schools featured here.',
  },
  // UMA ad copy that hands off to UMA's own request-info page on this site
  // instead of the Clicks Search redirect.
  'uma-v3': {
    slug: 'uma-v3',
    brand: 'Ultimate Medical Academy',
    layout: 'branded',
    universitySlug: 'ultimate-medical-academy',
    destinationUrl: '/universities/ultimate-medical-academy',
    metaTitle: 'Ultimate Medical Academy Healthcare Programs',
    metaDescription:
      'Healthcare career training designed around your life. Explore online associate degree programs from Ultimate Medical Academy.',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/br5nxfXurTya7wJ6Dtqd98/Medical.png',
    heroImageAlt:
      'Healthcare professional in scrubs smiling in a clinical setting',
    cardHeadline: 'Interested in a career in healthcare?',
    cardSubhead:
      'Learn how UMA can help you prepare for the healthcare role you want',
    ctaLabel: 'Get Program Information',
    sectionHeading: 'Healthcare Education Built Around Your Life',
    paragraphs: [
      'UMA offers online associate degree programs across medical assisting, healthcare management, health sciences, medical billing and coding, and more — each designed to build practical, job-ready knowledge for the healthcare workforce.',
      'You’ll have support at every stage: advisors who help you choose a program, instructors who teach in an engaging online environment, and career services that help you prepare for interviews and your job search after graduation.',
    ],
    disclosure:
      'FindOnlineDegrees is an advertising-supported site. We may receive compensation when you request information from the schools featured here.',
  },
  // Brand-neutral version of the same template — no advertiser named, so
  // the copy speaks to matching against accredited schools generally.
  'search-v1': {
    slug: 'search-v1',
    brand: 'FindOnlineDegrees',
    clicksSearch: {
      defaultPubGroupID: 'PCGP3406855604',
    },
    metaTitle: 'Find Your Online Degree Program | FindOnlineDegrees',
    metaDescription:
      'Compare accredited online degree programs and get matched with schools that fit your goals, schedule, and budget.',
    heroImage:
      'https://cdn.magicpatterns.com/patterns/generated-images/17c98d59-2e1d-42a3-b403-680119e82ff3.jpg',
    heroImageAlt:
      'Adult student smiling while taking an online class on a laptop at home',
    cardHeadline: 'Get Matched With Your Perfect School Today!',
    cardSubhead: 'Financial aid may be available for those who qualify',
    ctaLabel: 'Start Now!',
    sectionHeading: 'Find the Online Program That Fits Your Life',
    paragraphs: [
      'Going back to school looks different for everyone. We help you compare accredited online programs side by side — across business, healthcare, technology, education, and more — so you can see which schools actually fit your schedule, your budget, and where you want to go next.',
      'Answer a few quick questions and we’ll match you with schools currently enrolling students in the programs you care about. There’s no cost to explore your options, and many students qualify for financial aid.',
    ],
    disclosure:
      'FindOnlineDegrees is an advertising-supported site. We may receive compensation when you request information from the schools featured here.',
  },
}
export const getExploreBridge = (slug: string | undefined) =>
  slug ? exploreBridges[slug.toLowerCase()] : undefined
// Used when the route is previewed without a real slug.
export const getDefaultExploreBridge = () => Object.values(exploreBridges)[0]
